import type { FC } from "react";
import { useState } from "react";

import { CustomButton, CustomInput } from "@/components/atoms";

interface AmountSubMenuProps {
  category: string;
  onApply: (
    category: string,
    value: string | { from?: string; to?: string }
  ) => void;
  initialValue?: string;
  initialRange?: {
    from?: string;
    to?: string;
  };
  setIsOpen: (open: boolean) => void;
  range?: boolean;
}

const AmountSubMenu: FC<AmountSubMenuProps> = ({
  category,
  onApply,
  initialValue = "",
  initialRange,
  setIsOpen,
  range = false,
}) => {
  const [singleValue, setSingleValue] = useState(initialValue);
  const [from, setFrom] = useState(initialRange?.from ?? "");
  const [to, setTo] = useState(initialRange?.to ?? "");

  const handleApply = () => {
    if (range) {
      if (!from && !to) return;

      const normalizedFrom = to && !from ? "0" : from || undefined;
      const normalizedTo = to || undefined;

      onApply(category, {
        from: normalizedFrom,
        to: normalizedTo,
      });
    } else {
      if (!singleValue.trim()) return;
      onApply(category, singleValue.trim());
    }

    setIsOpen(false);
  };

  const allowOnlyPositiveDigits = (value: string) => {
    if (value === "") return true;
    return /^\d+$/.test(value);
  };

  return (
    <div className="px-2 py-2 flex flex-col gap-2">
      {range ? (
        <>
          <CustomInput
            placeholder="From"
            value={from}
            onChange={e => {
              const value = e.target.value;
              if (!allowOnlyPositiveDigits(value)) return;
              setFrom(value);
            }}
            className="h-8 text-sm"
            data-testid="amount-submenu-input-from"
          />
          <CustomInput
            placeholder="To"
            value={to}
            onChange={e => {
              const value = e.target.value;
              if (!allowOnlyPositiveDigits(value)) return;

              setTo(value);

              if (value && !from) {
                setFrom("0");
              }
            }}
            className="h-8 text-sm"
            data-testid="amount-submenu-input-to"
          />
        </>
      ) : (
        <CustomInput
          placeholder="50,000"
          value={singleValue}
          onChange={e => {
            const value = e.target.value;
            if (!allowOnlyPositiveDigits(value)) return;
            setSingleValue(value);
          }}
          className="h-8 text-sm"
          data-testid="amount-submenu-input"
        />
      )}

      <CustomButton
        size="sm"
        variant="default"
        onClick={handleApply}
        className="font-[Geist] text-xs font-medium px-[10px]! py-[5px]! w-fit h-7"
        data-testid="amount-submenu-apply-button"
      >
        Apply
      </CustomButton>
    </div>
  );
};

export default AmountSubMenu;
